"use client";

import { useEffect, useState, useCallback } from "react";

interface Banner {
  id: number;
  imageUrl: string;
  linkUrl: string;
}

// Fisher-Yates shuffle algorithm - ensures fair random distribution
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function SponsoredBanner() {
  const [originalBanners, setOriginalBanners] = useState<Banner[]>([]);
  const [shuffledBanners, setShuffledBanners] = useState<Banner[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fetch banners and shuffle them on initial load
  useEffect(() => {
    fetch("/api/admin/banner")
      .then((r) => r.json())
      .then((data) => {
        const fetchedBanners = data.banners || [];
        setOriginalBanners(fetchedBanners);
        setShuffledBanners(shuffleArray(fetchedBanners));
      })
      .catch(() => {});
  }, []);

  // Reshuffle when reaching end of cycle
  const advanceToNext = useCallback(() => {
    setCurrentIndex((prev) => {
      if (prev >= shuffledBanners.length - 1) {
        // Reached end of cycle - reshuffle for new random order
        setShuffledBanners(shuffleArray(originalBanners));
        return 0;
      }
      return prev + 1;
    });
  }, [shuffledBanners.length, originalBanners]);

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (shuffledBanners.length <= 1) return;

    const interval = setInterval(advanceToNext, 5000);
    return () => clearInterval(interval);
  }, [shuffledBanners.length, advanceToNext]);

  // Safety check for index bounds
  useEffect(() => {
    if (currentIndex >= shuffledBanners.length && shuffledBanners.length > 0) {
      setCurrentIndex(0);
    }
  }, [currentIndex, shuffledBanners.length]);

  if (shuffledBanners.length === 0) return null;

  const banner = shuffledBanners[currentIndex];
  if (!banner) return null;

  const trackClick = (bannerId: number) => {
    fetch(`/api/banners/${bannerId}/click`, { method: "POST" }).catch(() => {});
  };

  return (
    <div className="relative w-full overflow-hidden rounded-xl" style={{ aspectRatio: "3/1" }}>
      <a
        href={banner.linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block h-full w-full"
        onClick={() => trackClick(banner.id)}
      >
        <img
          src={banner.imageUrl}
          alt="Sponsored"
          className="h-full w-full object-cover"
        />
      </a>
      {shuffledBanners.length > 1 && (
        <div className="absolute bottom-1.5 left-0 right-0 flex justify-center gap-1">
          {shuffledBanners.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={(event) => {
                event.preventDefault();
                setCurrentIndex(index);
              }}
              className="h-1.5 w-1.5 rounded-full transition-all"
              style={{ background: index === currentIndex ? "#fff" : "rgba(255,255,255,0.4)" }}
              aria-label={`View sponsored banner ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
